import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaCalendarAlt, FaUsers, FaShieldAlt, FaCheckCircle, FaPhone } from 'react-icons/fa';

const AggressivePatientForm = ({ onSubmit }) => {
  const [form, setForm] = useState({
    incidentDate: new Date().toISOString().split('T')[0],
    incidentTime: '',
    location: '',
    involvedPartyType: 'Patient',
    staffInvolved: '',
    behaviors: [],
    weaponsPresent: 'No',
    injuryOccurred: 'No',
    injuryDetails: '',
    deescalationMethods: [],
    lawEnforcementInvolved: 'No',
    outcome: '',
    followUpNeeded: 'No',
    followUpPlan: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggleInArray = (key, value) => {
    setForm((s) => {
      const exists = s[key].includes(value);
      const next = exists ? s[key].filter((v) => v !== value) : [...s[key], value];
      return { ...s, [key]: next };
    });
  };

  const update = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit(form);
      setIsSubmitting(false);
    }, 100);
  };

  const behaviors = ['Verbal Abuse', 'Threats', 'Physical Aggression', 'Property Damage', 'Harassment', 'Refusal to Leave'];
  const deescalationMethods = ['Verbal De-escalation', 'Manager Involved', 'Security Called', 'Law Enforcement', 'Patient Removed', 'Other'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaShieldAlt className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Aggressive Patient Interaction
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report aggressive patient interactions and document incident details
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={submit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and where the incident occurred
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="incidentDate" className="block text-sm/6 font-medium text-gray-900">
                      Date of Incident <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="incidentDate"
                        name="incidentDate"
                        type="date"
                        value={form.incidentDate}
                        onChange={update}
                        required
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="incidentTime" className="block text-sm/6 font-medium text-gray-900">
                      Time of Incident
                    </label>
                    <div className="mt-2">
                      <input
                        id="incidentTime"
                        name="incidentTime"
                        type="time"
                        value={form.incidentTime}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="location" className="block text-sm/6 font-medium text-gray-900">
                      Location/Operatory
                    </label>
                    <div className="mt-2">
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={form.location}
                        onChange={update}
                        placeholder="Enter location or operatory"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Involved Parties Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUsers className="size-5 text-gray-400" />
                  Involved Parties
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Information about parties involved in the incident
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="involvedPartyType" className="block text-sm/6 font-medium text-gray-900">
                      Involved Party Type
                    </label>
                    <div className="mt-2">
                      <select
                        id="involvedPartyType"
                        name="involvedPartyType"
                        value={form.involvedPartyType}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option>Patient</option>
                        <option>Family/Visitor</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="staffInvolved" className="block text-sm/6 font-medium text-gray-900">
                      Staff Involved
                    </label>
                    <div className="mt-2">
                      <input
                        id="staffInvolved"
                        name="staffInvolved"
                        type="text"
                        value={form.staffInvolved}
                        onChange={update}
                        placeholder="Enter staff members involved"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Behavioral Assessment Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Behavioral Assessment
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Document observed behaviors and safety concerns
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">
                    Observed Behaviors
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {behaviors.map((behavior) => (
                      <div key={behavior} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              checked={form.behaviors.includes(behavior)}
                              onChange={() => toggleInArray('behaviors', behavior)}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 indeterminate:border-violet-600 indeterminate:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <svg
                              fill="none"
                              viewBox="0 0 14 14"
                              className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                            >
                              <path
                                d="M3 8L6 11L11 3.5"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-[:checked]:opacity-100"
                              />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                          {behavior}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="weaponsPresent" className="block text-sm/6 font-medium text-gray-900">
                      Weapons Present
                    </label>
                    <div className="mt-2">
                      <select
                        id="weaponsPresent"
                        name="weaponsPresent"
                        value={form.weaponsPresent}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="injuryOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Injury Occurred
                    </label>
                    <div className="mt-2">
                      <select
                        id="injuryOccurred"
                        name="injuryOccurred"
                        value={form.injuryOccurred}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>

                {form.injuryOccurred === 'Yes' && (
                  <div className="mt-6">
                    <label htmlFor="injuryDetails" className="block text-sm/6 font-medium text-gray-900">
                      Injury Details
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="injuryDetails"
                        name="injuryDetails"
                        rows={4}
                        value={form.injuryDetails}
                        onChange={update}
                        placeholder="Provide details about any injuries sustained..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Response Actions Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaShieldAlt className="size-5 text-gray-400" />
                  Response Actions
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  De-escalation methods and response actions taken
                </p>

                <div className="mt-10">
                  <label className="block text-sm/6 font-medium text-gray-900 mb-4">
                    De-escalation Methods
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {deescalationMethods.map((method) => (
                      <div key={method} className="flex gap-3">
                        <div className="flex h-6 shrink-0 items-center">
                          <div className="group grid size-4 grid-cols-1">
                            <input
                              type="checkbox"
                              checked={form.deescalationMethods.includes(method)}
                              onChange={() => toggleInArray('deescalationMethods', method)}
                              className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 indeterminate:border-violet-600 indeterminate:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <svg
                              fill="none"
                              viewBox="0 0 14 14"
                              className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                            >
                              <path
                                d="M3 8L6 11L11 3.5"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-[:checked]:opacity-100"
                              />
                            </svg>
                          </div>
                        </div>
                        <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                          {method}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lawEnforcementInvolved" className="block text-sm/6 font-medium text-gray-900">
                      Law Enforcement Involved
                    </label>
                    <div className="mt-2">
                      <select
                        id="lawEnforcementInvolved"
                        name="lawEnforcementInvolved"
                        value={form.lawEnforcementInvolved}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="outcome" className="block text-sm/6 font-medium text-gray-900">
                      Outcome
                    </label>
                    <div className="mt-2">
                      <select
                        id="outcome"
                        name="outcome"
                        value={form.outcome}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option value="">Select outcome</option>
                        <option>Resolved</option>
                        <option>Patient Removed</option>
                        <option>Appointment Terminated</option>
                        <option>Police Report Filed</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Follow-up Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPhone className="size-5 text-gray-400" />
                  Follow-up Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Follow-up requirements and plans
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="followUpNeeded" className="block text-sm/6 font-medium text-gray-900">
                      Follow-up Needed
                    </label>
                    <div className="mt-2">
                      <select
                        id="followUpNeeded"
                        name="followUpNeeded"
                        value={form.followUpNeeded}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>

                  {form.followUpNeeded === 'Yes' && (
                    <div>
                      <label htmlFor="followUpPlan" className="block text-sm/6 font-medium text-gray-900">
                        Follow-up Plan
                      </label>
                      <div className="mt-2">
                        <textarea
                          id="followUpPlan"
                          name="followUpPlan"
                          rows={4}
                          value={form.followUpPlan}
                          onChange={update}
                          placeholder="Describe the follow-up plan and next steps..."
                          className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AggressivePatientForm;
